import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { apiErrorMsg, formatBRL, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Kanban, Sparkles, User, Handshake, XCircle, RefreshCcw, MapPin, MessageSquare, Search, FileText, Repeat, ChevronRight, Target, HeartHandshake } from "lucide-react";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors,
  useDroppable, useDraggable, closestCenter,
} from "@dnd-kit/core";
import IndicarCelModal from "@/components/IndicarCelModal";
import PropostaModal from "@/components/PropostaModal";

// Iter 59 · 3 funis
const FUNIS = {
  1: {
    key: "prospeccao_fechamento",
    label: "Prospecção e Fechamento",
    short: "Prospecção",
    icon: Target,
    accent: "sky",
    estagios: ["indicacao", "prospeccao", "qualificado", "reuniao", "proposta", "documentacao", "contrato_assinado", "perdido"],
  },
  2: {
    key: "requalificacao",
    label: "Requalificação",
    short: "Requalificação",
    icon: Repeat,
    accent: "fuchsia",
    estagios: ["entrada", "nutricao", "monitoramento_gatilho", "gatilho_ativado", "requalificado"],
  },
  3: {
    key: "pos_venda",
    label: "Pós-Venda e Expansão",
    short: "Pós-Venda",
    icon: HeartHandshake,
    accent: "emerald",
    estagios: ["acompanhamento", "satisfacao", "diagnostico_expansao", "cross_sell", "indicacao_pos", "encerramento"],
  },
};

const ESTAGIO_META = {
  // N1
  indicacao: { label: "Indicação", head: "bg-sky-500", pill: "bg-sky-100 text-sky-800" },
  prospeccao: { label: "Prospecção", head: "bg-sky-600", pill: "bg-sky-100 text-sky-800" },
  qualificado: { label: "Qualificado", head: "bg-cyan-600", pill: "bg-cyan-100 text-cyan-800" },
  reuniao: { label: "Reunião", head: "bg-indigo-600", pill: "bg-indigo-100 text-indigo-800" },
  proposta: { label: "Proposta", head: "bg-violet-600", pill: "bg-violet-100 text-violet-800" },
  documentacao: { label: "Documentação", head: "bg-amber-500", pill: "bg-amber-100 text-amber-800" },
  contrato_assinado: { label: "Contrato Assinado", head: "bg-emerald-600", pill: "bg-emerald-100 text-emerald-800" },
  perdido: { label: "Perdido", head: "bg-red-600", pill: "bg-red-100 text-red-800" },
  // N2
  entrada: { label: "Entrada", head: "bg-fuchsia-500", pill: "bg-fuchsia-100 text-fuchsia-800" },
  nutricao: { label: "Nutrição", head: "bg-fuchsia-600", pill: "bg-fuchsia-100 text-fuchsia-800" },
  monitoramento_gatilho: { label: "Mon. Gatilho", head: "bg-purple-600", pill: "bg-purple-100 text-purple-800" },
  gatilho_ativado: { label: "Gatilho Ativado", head: "bg-pink-600", pill: "bg-pink-100 text-pink-800" },
  requalificado: { label: "Requalificado", head: "bg-rose-600", pill: "bg-rose-100 text-rose-800" },
  // N3
  acompanhamento: { label: "Acompanhamento", head: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-800" },
  satisfacao: { label: "Satisfação", head: "bg-teal-500", pill: "bg-teal-100 text-teal-800" },
  diagnostico_expansao: { label: "Diag. Expansão", head: "bg-teal-600", pill: "bg-teal-100 text-teal-800" },
  cross_sell: { label: "Cross-sell", head: "bg-green-600", pill: "bg-green-100 text-green-800" },
  indicacao_pos: { label: "Indicação", head: "bg-lime-600", pill: "bg-lime-100 text-lime-800" },
  encerramento: { label: "Encerramento", head: "bg-slate-600", pill: "bg-slate-100 text-slate-800" },
};

const STAGES_COM_VALOR = new Set(["proposta", "documentacao", "contrato_assinado", "requalificado", "acompanhamento", "satisfacao", "diagnostico_expansao", "cross_sell"]);

const N1_ESTAGIOS_DESTINO_REQUALIFICACAO = ["indicacao", "prospeccao", "qualificado", "reuniao", "proposta", "documentacao"];

function Card({ card, onOpen, canDrag = true }) {
  const isLeadAcao = card.origem_tipo === "lead_acao";
  const isProsp = card.origem_tipo === "prospeccao_carteira";
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, disabled: !canDrag });
  const style = isDragging ? { opacity: 0.4 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      onClick={() => onOpen(card)}
      className={`bg-white rounded-lg border border-slate-200 p-2 shadow-sm hover:shadow-md hover:border-sky-400 transition touch-none ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
      data-testid={`cel-card-${card.id}`}
    >
      {(isLeadAcao || isProsp) && (
        <div className="mb-1 flex flex-wrap gap-1">
          {isLeadAcao && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-fuchsia-100 text-fuchsia-800 text-[9px] font-bold uppercase tracking-wider">📣 Lead</span>}
          {isProsp && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase tracking-wider">🎯 Prospecção</span>}
        </div>
      )}
      <div className="flex items-start gap-1">
        <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-900 text-xs truncate">{card.municipio_nome}/{card.uf}</div>
          <div className="text-[10px] text-slate-500 truncate">{card.tipo_orgao || "—"}</div>
        </div>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px]">
        <div className="text-slate-500 truncate max-w-[60%]" title={`Indicado por ${card.indicador_nome}`}>
          <User className="inline w-2.5 h-2.5 mr-0.5" /> {card.indicador_nome?.split(" ")[0]}
        </div>
        {(card.valor_fechado || STAGES_COM_VALOR.has(card.estagio) || STAGES_COM_VALOR.has(card.pos_venda_estagio)) ? (
          <div className="font-bold text-slate-800 text-[11px]">{formatBRL(card.valor_fechado || card.valor_estimado)}</div>
        ) : null}
      </div>
      {card.vendedora_nome && (
        <div className="mt-0.5 text-[9px] text-slate-500 truncate">Resp: <b>{card.vendedora_nome}</b></div>
      )}
    </div>
  );
}

function Coluna({ estagio, cards, metric, onOpen, canDragCard }) {
  const meta = ESTAGIO_META[estagio];
  const { setNodeRef, isOver } = useDroppable({ id: estagio });
  const showValor = STAGES_COM_VALOR.has(estagio);
  return (
    <div ref={setNodeRef} className={`flex flex-col bg-slate-50 rounded-xl border transition min-w-0 ${isOver ? "border-sky-500 ring-2 ring-sky-300" : "border-slate-200"}`} data-testid={`col-${estagio}`}>
      <div className={`${meta.head} text-white px-2 py-1.5 rounded-t-xl`}>
        <div className="text-[11px] font-bold uppercase tracking-wider truncate">{meta.label}</div>
        <div className="text-[9px] opacity-90 truncate">{metric.qtd}{showValor ? ` · ${formatBRL(metric.valor_total)}` : ""}</div>
      </div>
      <div className="p-1.5 space-y-1.5 overflow-y-auto max-h-[calc(100vh-320px)] min-h-[80px]">
        {cards.length === 0 && (<div className="text-center text-[10px] text-slate-400 py-4">Vazio</div>)}
        {cards.map((c) => <Card key={c.id} card={c} onOpen={onOpen} canDrag={canDragCard(c)} />)}
      </div>
    </div>
  );
}

// Trilha visual dos 3 níveis no topo — clicável, mostra qtd + valor por funil
function FunilTrail({ nivelAtivo, onChange, totais }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-0 items-stretch">
      {[1, 2, 3].map((n, idx) => {
        const cfg = FUNIS[n];
        const Icon = cfg.icon;
        const isActive = nivelAtivo === n;
        const t = totais?.[n] || { qtd: 0, valor_total: 0 };
        const bgActive = { sky: "bg-sky-600 text-white", fuchsia: "bg-fuchsia-600 text-white", emerald: "bg-emerald-600 text-white" }[cfg.accent];
        const bgIdle = "bg-white text-slate-700 hover:bg-slate-50";
        return (
          <div key={n} className="flex items-stretch">
            <button
              onClick={() => onChange(n)}
              className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl border transition text-left ${isActive ? `${bgActive} border-transparent shadow-md` : `${bgIdle} border-slate-200`}`}
              data-testid={`funil-tab-${n}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? "bg-white/20" : "bg-slate-100"}`}>
                <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-slate-500"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? "opacity-80" : "text-slate-400"}`}>Nível {n}</div>
                <div className="font-display font-bold text-sm truncate">{cfg.label}</div>
                <div className={`text-[11px] ${isActive ? "opacity-90" : "text-slate-500"}`}>
                  <b>{t.qtd}</b> negócios{t.valor_total ? ` · ${formatBRL(t.valor_total)}` : ""}
                </div>
              </div>
            </button>
            {idx < 2 && (
              <div className="hidden md:flex items-center px-1">
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DetalheModal({ card, onClose, onChanged, vendedorasCel, user, initialAction }) {
  const [busy, setBusy] = useState(false);
  const [contatoOpen, setContatoOpen] = useState(false);
  const [fecharOpen, setFecharOpen] = useState(false);
  const [perderOpen, setPerderOpen] = useState(false);
  const [requalificarOpen, setRequalificarOpen] = useState(false);
  const [enviarReqOpen, setEnviarReqOpen] = useState(false);
  const [propostaOpen, setPropostaOpen] = useState(false);
  const [ct, setCt] = useState({ canal: "ligacao", descricao: "", data_retorno: "" });
  const [fc, setFc] = useState({ valor_fechado: card?.valor_estimado || "", comissao_pct: "", observacoes: "" });
  const [pd, setPd] = useState({ motivo_categoria: "Sem interesse", motivo_obs: "" });
  const [rq, setRq] = useState({ estagio_destino: "prospeccao", observacoes: "" });
  const [envReq, setEnvReq] = useState({ motivo: "" });

  useEffect(() => {
    if (initialAction === "fechar") setFecharOpen(true);
    else if (initialAction === "perder") setPerderOpen(true);
    else if (initialAction === "requalificar") setRequalificarOpen(true);
  }, [initialAction]);

  if (!card) return null;

  const isGestao = user?.role === "diretoria" || user?.role === "gestao_cel";
  const isVendedoraDesta = user?.role === "vendedora" && card.vendedora_id === user?.id;
  const nivelCard = card.pos_venda_estagio ? 3 : (["entrada", "nutricao", "monitoramento_gatilho", "gatilho_ativado", "requalificado"].includes(card.estagio) ? 2 : 1);
  const podeAgir = (isGestao || isVendedoraDesta) && card.estagio !== "perdido";
  const meta = ESTAGIO_META[nivelCard === 3 ? card.pos_venda_estagio : card.estagio];
  const historicoOrd = [...(card.historico || [])].sort((a, b) => (b.data > a.data ? 1 : -1));
  const estagiosMoveMenu = FUNIS[nivelCard].estagios.filter((e) => {
    if (nivelCard === 1) return !["contrato_assinado", "perdido", card.estagio].includes(e);
    if (nivelCard === 2) return !["requalificado", card.estagio].includes(e); // requalificado exige endpoint dedicado
    return e !== card.pos_venda_estagio;
  });

  const moverEstagio = async (novo) => {
    setBusy(true);
    try {
      let r;
      if (nivelCard === 3) {
        r = await api.patch(`/cel/indicacoes/${card.id}/pos-venda`, { pos_venda_estagio: novo });
      } else {
        r = await api.patch(`/cel/indicacoes/${card.id}`, { estagio: novo });
      }
      toast.success(`Movido para: ${ESTAGIO_META[novo].label}`);
      onChanged?.(r.data);
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setBusy(false); }
  };

  const atribuirVendedora = async (vid) => {
    setBusy(true);
    try {
      const r = await api.patch(`/cel/indicacoes/${card.id}`, { vendedora_id: vid });
      toast.success("Vendedora atribuída.");
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  const registrarContato = async () => {
    if (!ct.descricao.trim()) { toast.error("Descreva o contato."); return; }
    setBusy(true);
    try {
      await api.post(`/cel/indicacoes/${card.id}/registrar-contato`, {
        canal: ct.canal, descricao: ct.descricao.trim(),
        data_retorno: ct.data_retorno || null,
      });
      toast.success("Contato registrado.");
      setCt({ canal: "ligacao", descricao: "", data_retorno: "" });
      setContatoOpen(false);
      const r = await api.get(`/cel/indicacoes/${card.id}`);
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  const fecharContrato = async () => {
    const v = Number(fc.valor_fechado);
    if (!v || v <= 0) { toast.error("Informe o valor do contrato."); return; }
    setBusy(true);
    try {
      const payload = { valor_fechado: v, observacoes: fc.observacoes || null };
      if (fc.comissao_pct) payload.comissao_pct = Number(fc.comissao_pct);
      const r = await api.post(`/cel/indicacoes/${card.id}/fechar-contrato`, payload);
      toast.success(`🎉 Contrato fechado! Comissão: ${formatBRL(r.data.comissao_valor)}`);
      setFecharOpen(false);
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  const marcarPerdido = async () => {
    if (!pd.motivo_categoria.trim()) { toast.error("Selecione um motivo."); return; }
    setBusy(true);
    try {
      await api.post(`/cel/indicacoes/${card.id}/marcar-perdido`, pd);
      toast.success("Indicação marcada como perdida.");
      setPerderOpen(false);
      const r = await api.get(`/cel/indicacoes/${card.id}`);
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  const enviarParaRequalificacao = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cel/indicacoes/${card.id}/enviar-requalificacao`, { motivo: envReq.motivo || null });
      toast.success("Enviado para Requalificação (N2 · Entrada)");
      setEnviarReqOpen(false);
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  const requalificar = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cel/indicacoes/${card.id}/requalificar`, {
        estagio_destino: rq.estagio_destino,
        observacoes: rq.observacoes || null,
      });
      toast.success(`Requalificado — voltou pro N1 · ${ESTAGIO_META[rq.estagio_destino].label}`);
      setRequalificarOpen(false);
      onChanged?.(r.data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cel-detalhe-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <MapPin className="w-5 h-5" /> {card.municipio_nome} / {card.uf}
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${meta.pill}`}>{meta.label}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold uppercase">Nível {nivelCard} · {FUNIS[nivelCard].short}</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Indicado por <b>{card.indicador_nome}</b> ({card.indicador_role}) em {formatDateTime(card.criado_em)}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {(STAGES_COM_VALOR.has(card.estagio) || STAGES_COM_VALOR.has(card.pos_venda_estagio)) && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Valor estimado</div>
                <div className="font-bold text-slate-800">{formatBRL(card.valor_estimado)}</div>
              </div>
            )}
            {card.valor_fechado && (
              <>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Valor fechado</div>
                  <div className="font-bold text-emerald-800">{formatBRL(card.valor_fechado)}</div>
                </div>
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Comissão indicador ({card.comissao_pct}%)</div>
                  <div className="font-bold text-emerald-900">{formatBRL(card.comissao_valor)}</div>
                </div>
                {card.comissao_vendedora_valor > 0 && (
                  <div className="bg-sky-100 p-3 rounded-lg">
                    <div className="text-[10px] uppercase font-bold text-sky-700 tracking-wider">Comissão vendedora ({card.comissao_vendedora_pct}%)</div>
                    <div className="font-bold text-sky-900">{formatBRL(card.comissao_vendedora_valor)}</div>
                  </div>
                )}
              </>
            )}
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Contato</div>
              <div className="font-semibold text-slate-800 text-xs">{card.contato_nome || "—"}</div>
              <div className="text-[11px] text-slate-500">{card.contato_cargo || ""} {card.contato_whatsapp ? `· ${card.contato_whatsapp}` : ""}</div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">CNPJ · E-mail</div>
              <div className="text-xs">{card.cnpj || "—"}</div>
              <div className="text-[11px] text-slate-500 truncate">{card.email || ""}</div>
            </div>
          </div>

          {card.observacoes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-slate-700">
              <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">Observações do indicador</div>
              {card.observacoes}
            </div>
          )}

          {podeAgir && (
            <div className="border rounded-lg p-3 space-y-3 bg-slate-50" data-testid="acoes-gestao">
              <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">Ações</div>
              <div className="flex flex-wrap gap-2 items-center">
                {isGestao && nivelCard !== 3 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Vendedora:</Label>
                    <Select value={card.vendedora_id || ""} onValueChange={atribuirVendedora}>
                      <SelectTrigger className="w-56 h-8" data-testid="select-vendedora"><SelectValue placeholder="Atribuir..." /></SelectTrigger>
                      <SelectContent>
                        {vendedorasCel.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {estagiosMoveMenu.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Mover para:</Label>
                    <Select value="" onValueChange={moverEstagio}>
                      <SelectTrigger className="w-56 h-8" data-testid="select-estagio"><SelectValue placeholder="Escolher estágio..." /></SelectTrigger>
                      <SelectContent>
                        {estagiosMoveMenu.map((e) => (<SelectItem key={e} value={e}>{ESTAGIO_META[e].label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                <Button size="sm" variant="outline" onClick={() => setContatoOpen(true)} data-testid="btn-registrar-contato">
                  <MessageSquare className="w-4 h-4 mr-1" /> Registrar contato
                </Button>
                {nivelCard === 1 && card.estagio !== "contrato_assinado" && (
                  <>
                    <Button size="sm" variant="outline" className="text-sky-700 border-sky-300 hover:bg-sky-50" onClick={() => setPropostaOpen(true)} data-testid="btn-gerar-proposta">
                      <FileText className="w-4 h-4 mr-1" /> Gerar proposta
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setFecharOpen(true)} data-testid="btn-abrir-fechar">
                      <Handshake className="w-4 h-4 mr-1" /> Fechar contrato
                    </Button>
                    <Button size="sm" variant="outline" className="text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50" onClick={() => setEnviarReqOpen(true)} data-testid="btn-enviar-req">
                      <Repeat className="w-4 h-4 mr-1" /> Enviar p/ Requalificação
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setPerderOpen(true)} data-testid="btn-abrir-perder">
                      <XCircle className="w-4 h-4 mr-1" /> Marcar perdido
                    </Button>
                  </>
                )}
                {nivelCard === 2 && card.estagio !== "requalificado" && (
                  <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => setRequalificarOpen(true)} data-testid="btn-requalificar">
                    <Repeat className="w-4 h-4 mr-1" /> Requalificar → N1
                  </Button>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-2">Histórico</div>
            <div className="border-l-2 border-slate-200 pl-3 space-y-2 max-h-[280px] overflow-y-auto">
              {historicoOrd.map((e, i) => (
                <div key={i} className="text-xs">
                  <div className="text-slate-500">{formatDateTime(e.data)} · <b className="text-slate-700">{e.autor_nome}</b> · <span className="text-slate-400 uppercase">{e.tipo}</span></div>
                  <div className="text-slate-800">{e.descricao}</div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registrar contato */}
      <Dialog open={contatoOpen} onOpenChange={setContatoOpen}>
        <DialogContent data-testid="modal-registrar-contato">
          <DialogHeader><DialogTitle>Registrar contato</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Canal</Label>
              <Select value={ct.canal} onValueChange={(v) => setCt({ ...ct, canal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ligacao">Ligação</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="reuniao">Reunião</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>O que foi conversado *</Label>
              <Textarea value={ct.descricao} onChange={(e) => setCt({ ...ct, descricao: e.target.value })} rows={4} data-testid="ct-descricao" />
            </div>
            <div>
              <Label>Retornar em (opcional)</Label>
              <Input type="date" value={ct.data_retorno} onChange={(e) => setCt({ ...ct, data_retorno: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContatoOpen(false)}>Cancelar</Button>
            <Button onClick={registrarContato} disabled={busy} data-testid="ct-submit">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fechar contrato */}
      <Dialog open={fecharOpen} onOpenChange={setFecharOpen}>
        <DialogContent data-testid="modal-fechar-contrato">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">🎉 Fechar contrato CeL</DialogTitle>
            <DialogDescription>Card também irá para o funil N3 · Pós-Venda automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Valor fechado (R$) *</Label>
              <Input type="number" min="0" step="1000" value={fc.valor_fechado} onChange={(e) => setFc({ ...fc, valor_fechado: e.target.value })} data-testid="fc-valor" />
            </div>
            {isGestao && (
              <div>
                <Label>Comissão (%) — vazio usa padrão</Label>
                <Input type="number" min="0" max="100" step="0.5" value={fc.comissao_pct} onChange={(e) => setFc({ ...fc, comissao_pct: e.target.value })} placeholder="Padrão do sistema" data-testid="fc-pct" />
              </div>
            )}
            <div>
              <Label>Observações</Label>
              <Textarea rows={3} value={fc.observacoes} onChange={(e) => setFc({ ...fc, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFecharOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={fecharContrato} disabled={busy} data-testid="fc-submit">
              <Handshake className="w-4 h-4 mr-1" /> Confirmar fechamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Perder */}
      <Dialog open={perderOpen} onOpenChange={setPerderOpen}>
        <DialogContent data-testid="modal-perder">
          <DialogHeader><DialogTitle>Marcar como perdido</DialogTitle><DialogDescription>Motivo é obrigatório.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo *</Label>
              <Select value={pd.motivo_categoria} onValueChange={(v) => setPd({ ...pd, motivo_categoria: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sem interesse">Sem interesse</SelectItem>
                  <SelectItem value="Sem orçamento">Sem orçamento</SelectItem>
                  <SelectItem value="Concorrência">Escolheu concorrente</SelectItem>
                  <SelectItem value="Timing">Timing / momento inadequado</SelectItem>
                  <SelectItem value="Sem retorno">Sem retorno</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={3} value={pd.motivo_obs} onChange={(e) => setPd({ ...pd, motivo_obs: e.target.value })} data-testid="pd-obs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPerderOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={marcarPerdido} disabled={busy} data-testid="pd-submit">Confirmar perda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enviar para requalificação (N1 → N2) */}
      <Dialog open={enviarReqOpen} onOpenChange={setEnviarReqOpen}>
        <DialogContent data-testid="modal-enviar-req">
          <DialogHeader>
            <DialogTitle className="text-fuchsia-700">Enviar para Requalificação (N2)</DialogTitle>
            <DialogDescription>O card vai para o funil N2 · Entrada. Você pode trazê-lo de volta a qualquer momento arrastando para "Requalificado" no N2.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo (opcional)</Label>
              <Textarea rows={3} value={envReq.motivo} onChange={(e) => setEnvReq({ motivo: e.target.value })} placeholder="Ex: cliente pediu prazo, empurrou 2x…" data-testid="envreq-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnviarReqOpen(false)}>Cancelar</Button>
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={enviarParaRequalificacao} disabled={busy} data-testid="envreq-submit">
              <Repeat className="w-4 h-4 mr-1" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Requalificar (N2 → N1, escolhe estágio) */}
      <Dialog open={requalificarOpen} onOpenChange={setRequalificarOpen}>
        <DialogContent data-testid="modal-requalificar">
          <DialogHeader>
            <DialogTitle className="text-fuchsia-700">Requalificar — voltar ao N1</DialogTitle>
            <DialogDescription>Escolha em qual estágio do funil de Prospecção este card deve retornar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Estágio destino no N1 *</Label>
              <Select value={rq.estagio_destino} onValueChange={(v) => setRq({ ...rq, estagio_destino: v })}>
                <SelectTrigger data-testid="rq-destino"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {N1_ESTAGIOS_DESTINO_REQUALIFICACAO.map((e) => (<SelectItem key={e} value={e}>{ESTAGIO_META[e].label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={3} value={rq.observacoes} onChange={(e) => setRq({ ...rq, observacoes: e.target.value })} data-testid="rq-obs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequalificarOpen(false)}>Cancelar</Button>
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={requalificar} disabled={busy} data-testid="rq-submit">
              <Repeat className="w-4 h-4 mr-1" /> Requalificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PropostaModal
        card={card}
        open={propostaOpen}
        onClose={() => setPropostaOpen(false)}
        onGerada={(res) => {
          const nextStage = ["indicacao","prospeccao","qualificado","reuniao"].includes(card.estagio) ? "proposta" : card.estagio;
          onChanged?.({ ...card, estagio: nextStage, valor_estimado: res?.valor_total ?? card.valor_estimado, proposta_atual: { id: res?.proposta_id, valor_total: res?.valor_total } });
        }}
      />
    </>
  );
}

export default function CelKanban() {
  const { user } = useAuth();
  const [nivelAtivo, setNivelAtivo] = useState(1);
  const [colunas, setColunas] = useState({});
  const [metricas, setMetricas] = useState({});
  const [totaisPorNivel, setTotaisPorNivel] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroUf, setFiltroUf] = useState("");
  const [openCard, setOpenCard] = useState(null);
  const [openCardInitialAction, setOpenCardInitialAction] = useState(null);
  const [vendedorasCel, setVendedorasCel] = useState([]);
  const [activeCardId, setActiveCardId] = useState(null);
  const [openIndicar, setOpenIndicar] = useState(false);
  const [busca, setBusca] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const isVendCel = user?.role === "vendedora" && user?.nucleo_nome === "CeL Consultoria";
  const isGestaoCel = user?.role === "gestao_cel";
  const isDiretoria = user?.role === "diretoria";

  const canDragCard = (card) => {
    if (isDiretoria || isGestaoCel) return true;
    if (isVendCel) return card.vendedora_id === user.id;
    return false;
  };

  const findCard = (id) => {
    for (const arr of Object.values(colunas)) {
      const found = (arr || []).find((c) => c.id === id);
      if (found) return found;
    }
    return null;
  };
  const activeCard = activeCardId ? findCard(activeCardId) : null;

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ nivel: String(nivelAtivo) });
    if (filtroUf) params.append("uf", filtroUf);
    api.get(`/cel/kanban?${params.toString()}`)
      .then((r) => {
        setColunas(r.data.colunas || {});
        setMetricas(r.data.metricas || {});
        setTotaisPorNivel(r.data.totais_por_nivel || {});
      })
      .catch(() => { setColunas({}); setMetricas({}); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroUf, nivelAtivo]);

  useEffect(() => {
    api.get("/users").then((r) => {
      const vs = (r.data || []).filter((u) => u.role === "vendedora" && u.nucleo_nome === "CeL Consultoria");
      setVendedorasCel(vs);
    }).catch(() => {});
    const bump = () => load();
    window.addEventListener("ws:cel_indicacao_nova", bump);
    window.addEventListener("ws:cel_indicacao_atualizada", bump);
    window.addEventListener("ws:cel_contrato_fechado", bump);
    return () => {
      window.removeEventListener("ws:cel_indicacao_nova", bump);
      window.removeEventListener("ws:cel_indicacao_atualizada", bump);
      window.removeEventListener("ws:cel_contrato_fechado", bump);
    };
    // eslint-disable-next-line
  }, []);

  const handleDragEnd = async (evt) => {
    setActiveCardId(null);
    const { active, over } = evt;
    if (!over) return;
    const card = findCard(active.id);
    if (!card) return;
    const targetCol = String(over.id);
    if (nivelAtivo === 3) {
      if (targetCol === card.pos_venda_estagio) return;
      if (!canDragCard(card)) { toast.error("Você só pode mover os cards atribuídos a você"); return; }
      try {
        const r = await api.patch(`/cel/indicacoes/${card.id}/pos-venda`, { pos_venda_estagio: targetCol });
        toast.success(`Movido para: ${ESTAGIO_META[targetCol]?.label}`);
        const nc = { ...colunas };
        nc[card.pos_venda_estagio] = (nc[card.pos_venda_estagio] || []).filter((c) => c.id !== card.id);
        nc[targetCol] = [r.data, ...(nc[targetCol] || [])];
        setColunas(nc);
      } catch (err) { toast.error(apiErrorMsg(err)); }
      return;
    }
    if (card.estagio === targetCol) return;
    if (!canDragCard(card)) { toast.error("Você só pode mover os cards atribuídos a você"); return; }
    if (nivelAtivo === 1 && targetCol === "contrato_assinado") {
      setOpenCard(card); setOpenCardInitialAction("fechar"); return;
    }
    if (nivelAtivo === 1 && targetCol === "perdido") {
      setOpenCard(card); setOpenCardInitialAction("perder"); return;
    }
    if (nivelAtivo === 2 && targetCol === "requalificado") {
      setOpenCard(card); setOpenCardInitialAction("requalificar"); return;
    }
    try {
      const r = await api.patch(`/cel/indicacoes/${card.id}`, { estagio: targetCol });
      toast.success(`Movido para: ${ESTAGIO_META[targetCol]?.label}`);
      const nc = { ...colunas };
      nc[card.estagio] = (nc[card.estagio] || []).filter((c) => c.id !== card.id);
      nc[targetCol] = [r.data, ...(nc[targetCol] || [])];
      setColunas(nc);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const ufsDisponiveis = useMemo(() => {
    const s = new Set();
    Object.values(colunas).forEach((arr) => (arr || []).forEach((c) => c.uf && s.add(c.uf)));
    return Array.from(s).sort();
  }, [colunas]);

  const { filteredColunas, filteredMetricas } = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return { filteredColunas: colunas, filteredMetricas: metricas };
    const fc = {}; const fm = {};
    for (const [est, cards] of Object.entries(colunas)) {
      const arr = (cards || []).filter((c) => (c.municipio_nome || "").toLowerCase().includes(q) || (c.uf || "").toLowerCase().includes(q));
      fc[est] = arr;
      fm[est] = {
        qtd: arr.length,
        valor_total: arr.reduce((s, c) => s + (Number(c.valor_fechado) || Number(c.valor_estimado) || 0), 0),
        label: metricas[est]?.label || est,
      };
    }
    return { filteredColunas: fc, filteredMetricas: fm };
  }, [busca, colunas, metricas]);

  const cfgAtivo = FUNIS[nivelAtivo];
  const nColunas = cfgAtivo.estagios.length;
  const gridColsCls = nColunas <= 5 ? "xl:grid-cols-5" : nColunas <= 6 ? "xl:grid-cols-6" : nColunas <= 7 ? "xl:grid-cols-7" : "xl:grid-cols-8";

  return (
    <div className="space-y-4" data-testid="cel-kanban-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Kanban className="w-7 h-7 text-sky-600" /> Funil CeL Consultoria
          </h1>
          <p className="text-slate-500 text-xs mt-1">3 níveis conectados: Prospecção → Requalificação → Pós-Venda</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input placeholder="Buscar município..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-8 w-56 h-9" data-testid="kanban-busca" />
          </div>
          {(isDiretoria || isGestaoCel || isVendCel) && (
            <Button size="sm" onClick={() => setOpenIndicar(true)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="btn-nova-oportunidade">
              <Sparkles className="w-4 h-4 mr-1" /> Nova oportunidade
            </Button>
          )}
          <Select value={filtroUf || "__all__"} onValueChange={(v) => setFiltroUf(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-32" data-testid="filter-uf"><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas UFs</SelectItem>
              {ufsDisponiveis.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh"><RefreshCcw className="w-4 h-4 mr-1" /> Atualizar</Button>
        </div>
      </div>

      <FunilTrail nivelAtivo={nivelAtivo} onChange={setNivelAtivo} totais={totaisPorNivel} />

      {loading ? (
        <div className="text-center py-16 text-slate-400">Carregando kanban...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => setActiveCardId(e.active.id)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveCardId(null)}
        >
          <div className={`grid grid-cols-2 md:grid-cols-3 ${gridColsCls} gap-2 pb-4`}>
            {cfgAtivo.estagios.map((est) => (
              <Coluna
                key={est}
                estagio={est}
                cards={filteredColunas[est] || []}
                metric={filteredMetricas[est] || { qtd: 0, valor_total: 0 }}
                onOpen={(c) => { setOpenCard(c); setOpenCardInitialAction(null); }}
                canDragCard={canDragCard}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard && (
              <div className="bg-white rounded-lg border border-sky-400 shadow-xl p-2 w-48 rotate-2">
                <div className="font-bold text-slate-900 text-xs truncate">{activeCard.municipio_nome}/{activeCard.uf}</div>
                <div className="text-[10px] text-slate-500">{formatBRL(activeCard.valor_estimado)}</div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {openCard && (
        <DetalheModal
          card={openCard}
          onClose={() => { setOpenCard(null); setOpenCardInitialAction(null); }}
          onChanged={(newCard) => { setOpenCard(newCard); load(); }}
          vendedorasCel={vendedorasCel}
          user={user}
          initialAction={openCardInitialAction}
        />
      )}

      <IndicarCelModal open={openIndicar} onClose={() => setOpenIndicar(false)} onCreated={() => { setOpenIndicar(false); load(); }} />
    </div>
  );
}
